import { effectiveLocale, enabledLocales, setEnabledLocales, type Locale } from "@salon-pos/shared";
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { en, type MessageKey } from "./en";
import { ms } from "./ms";
import { zh } from "./zh";
import { fill, onStaffNounChange } from "./noun";

export type { MessageKey } from "./en";
export const dictionaries: Record<Locale, Record<MessageKey, string>> = { en, zh, ms };
export const LOCALE_LABELS: Record<Locale, string> = { en: "English", zh: "中文", ms: "Bahasa Melayu" };

/** Locale used by non-React helpers (errorText, status labels). Kept in sync by the provider. */
let current: Locale = "en";
export const currentLocale = () => current;

export function translate(locale: Locale, key: MessageKey, vars: Record<string, string | number> = {}): string {
  const msg = dictionaries[locale]?.[key] ?? en[key] ?? key;
  return fill(locale, msg, vars);
}
export const tr = (key: MessageKey, vars?: Record<string, string | number>) => translate(current, key, vars);
export const hasKey = (key: string): key is MessageKey => key in en;

export type T = (key: MessageKey, vars?: Record<string, string | number>) => string;
const Ctx = createContext<{ locale: Locale; setLocale: (l: Locale) => void; t: T; locales: Locale[] }>({ locale: "en", setLocale: () => {}, t: (k) => en[k], locales: enabledLocales() });

const STORAGE_KEY = "spos.locale";

function initialLocale(): Locale {
  const saved = typeof localStorage !== "undefined" ? (localStorage.getItem(STORAGE_KEY) as Locale | null) : null;
  // (Task N) English by default, Chinese the alternative; a saved/browser "ms" falls back to English unless the platform enables it
  if (saved && saved in dictionaries) return effectiveLocale(saved);
  const nav = typeof navigator !== "undefined" ? navigator.language.toLowerCase() : "en";
  return nav.startsWith("zh") ? effectiveLocale("zh") : "en";
}

export function I18nProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(() => { current = initialLocale(); return current; });
  const setLocale = useCallback((l: Locale) => { const v = effectiveLocale(l); current = v; setLocaleState(v); localStorage.setItem(STORAGE_KEY, v); }, []);
  // (Task N) the platform's ENABLED_LOCALES (default en,zh) decides which languages the pickers offer
  const [locales, setLocales] = useState<Locale[]>(() => enabledLocales());
  useEffect(() => {
    let off = false;
    void loadPlatformLocales().then((list) => { if (off || !list) return; setLocales(list); setLocaleState((l) => { const v = effectiveLocale(l); current = v; return v; }); });
    return () => { off = true; };
  }, []);
  useEffect(() => { current = locale; document.documentElement.lang = locale === "zh" ? "zh-Hans" : locale; }, [locale]);
  // the salon's staff noun changed (industry): re-render every translated string
  const [nounRev, setNounRev] = useState(0);
  useEffect(() => onStaffNounChange(() => setNounRev((n) => n + 1)), []);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const value = useMemo(() => ({ locale, setLocale, locales, t: ((k, v) => translate(locale, k, v)) as T }), [locale, setLocale, locales, nounRev]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export const useI18n = () => useContext(Ctx);

/** Fetch /api/public/platform once (language flag); keeps the defaults when offline. */
let platformLoad: Promise<Locale[] | null> | null = null;
export function loadPlatformLocales(): Promise<Locale[] | null> {
  if (typeof fetch === "undefined") return Promise.resolve(null);
  platformLoad ??= (async () => {
    try {
      const { api } = await import("../api");
      const r = await api<{ locales?: Locale[] }>("/api/public/platform");
      if (!Array.isArray(r.locales)) return null;
      // (Task O) the store app ships English + 中文 only
      setEnabledLocales(import.meta.env.VITE_CLIENT === "native" ? r.locales.filter((l) => l !== "ms") : r.locales);
      return enabledLocales();
    } catch { return null; }
  })();
  return platformLoad;
}
/** Product languages offered in pickers (Task N: English + 中文 by default). */
export const useLocales = () => useContext(Ctx).locales;
